import { UserApi } from "@/api/user.api";
import { $account } from "@/stores/account";
import { CartService } from "@/stores/cart";
import { StytchUIClient } from "@stytch/vanilla-js";
import Cookies from "js-cookie";

// Replace this value with the public token found it your Stytch Dashboard.
const STYTCH_PUBLIC_TOKEN =
  process.env.REACT_APP_PROD ||
  window?.location?.host?.includes("tribecaprintworks")
    ? "public-token-live-4e1adc1f-8e98-4109-9e97-d901884312bf"
    : "public-token-test-b33f2a72-112d-483e-9540-8b28ca673078";

// Export stytch so that the other scripts in this application can interact with it.
export const StytchClient = new StytchUIClient(STYTCH_PUBLIC_TOKEN);

// 2026-08-28: guards against the wedged-session retry loop (one stuck
// browser sent the session ping + a failing Stytch authenticate dozens of
// times per SECOND for over an hour and pinned the database). Each session
// id is synced to the server once and authenticated once — a repeat with
// the same id is a no-op instead of another network call.
let lastSyncedSessionId: string | null = null;
let lastAuthenticatedSessionId: string | null = null;

function syncSessionOnce(
  session: ReturnType<typeof StytchClient.session.getSync>,
  user: ReturnType<typeof StytchClient.user.getSync>
) {
  const sid = session?.session_id ?? null;
  if (!sid || !user) return;
  if (sid === lastSyncedSessionId) return;
  lastSyncedSessionId = sid;
  UserApi.updateSession({ session, user }).catch(() => null);
}

function initialize() {
  // On page load, we check for the existence of a cached session.
  const session = StytchClient.session.getSync();
  const user = StytchClient.user.getSync();
  console.log(
    "[auth] initialize — session:",
    session ? `expires ${session.expires_at}` : "none",
    "| user:",
    user?.user_id ?? "none"
  );
  $account.setKey("isLoggedIn", !!session && !!user);

  if (session && user) {
    syncSessionOnce(session, user);
  }

  if (!Cookies.get("stytch_user_id") && session) {
    Cookies.set("stytch_user_id", session.user_id, { expires: 1 });
  }

  const unsubscribe = StytchClient.session.onChange(session => {
    const user = StytchClient.user.getSync();
    const wasLoggedIn = $account.get().isLoggedIn;
    const isLoggedIn = !!session && !!user;
    if (!wasLoggedIn && isLoggedIn) {
      const sid = session?.session_id ?? null;
      if (sid && sid !== lastAuthenticatedSessionId) {
        lastAuthenticatedSessionId = sid;
        // .catch: a 401 here means the cached session is dead; the SDK
        // clears it and the next onChange runs the logout branch. Without
        // the catch this was an unhandled rejection (Sentry 3D) firing in
        // a tight loop.
        StytchClient.session
          .authenticate({ session_duration_minutes: 10080 })
          .catch(() => null);
      }
    }
    const now = new Date().toISOString();
    console.log(
      `[auth] session onChange @ ${now} — session: ${session ? `expires ${session.expires_at}` : "null"} | user: ${user?.user_id ?? "null"} | wasLoggedIn: ${wasLoggedIn} → isLoggedIn: ${isLoggedIn}`,
      new Error().stack?.split("\n").slice(1, 4).join(" ")
    );
    if (session && user) {
      syncSessionOnce(session, user);
      Cookies.set("stytch_user_id", session.user_id, { expires: 1 });
      Cookies.set("stytch_user_email", user.emails[0].email, { expires: 1 });
    } else {
      console.warn(
        "[auth] logging out — removing cookies. session:",
        session,
        "user:",
        user
      );
      Cookies.remove("stytch_user_id");
      Cookies.remove("stytch_user_email");
      Cookies.remove("stytch_session_jwt");
    }
    CartService.getCart().catch(() => null);
    $account.setKey("isLoggedIn", isLoggedIn);
  });

  // On page close unsubscribe the onChange listener.
  window.addEventListener("beforeunload", () => {
    unsubscribe();
  });
}

initialize();
